import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { contractApi } from '../services/api';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  ArrowLeftIcon,
  PencilSquareIcon,
  DocumentArrowDownIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  XMarkIcon,
  PencilIcon,
  PlayIcon
} from '@heroicons/react/24/outline';

const ContractDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');

  useEffect(() => {
    fetchContract();
  }, [id]);

  const fetchContract = async () => {
    setLoading(true);
    try {
      const response = await contractApi.getById(id);
      setContract(response.data);
      setEmailAddress(response.data.tenantEmail || '');
    } catch (error) {
      toast.error('Échec du chargement du contrat');
      console.error(error);
      navigate('/contracts');
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePDF = async () => {
    try {
      await contractApi.generatePDF(id);
      toast.success('PDF généré avec succès');
      fetchContract(); // Refresh to get updated PDF path
    } catch (error) {
      toast.error('Échec de la génération du PDF');
      console.error(error);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      await contractApi.download(id);
      toast.success('PDF téléchargé avec succès');
    } catch (error) {
      toast.error('Échec du téléchargement du PDF. Veuillez d\'abord générer le PDF.');
      console.error(error);
    }
  };

  const handleSendEmail = async () => {
    if (!emailAddress) {
      toast.error('Veuillez saisir une adresse email');
      return;
    }

    try {
      await contractApi.sendEmail(id, emailAddress);
      toast.success('Email envoyé avec succès');
      setEmailModalOpen(false);
    } catch (error) {
      toast.error("Échec de l'envoi de l'email");
      console.error(error);
    }
  };

  const handleActivateContract = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir activer ce contrat ? Cette action est irréversible.')) {
      return;
    }

    try {
      await contractApi.activate(id);
      toast.success('Contrat activé avec succès');
      fetchContract(); // Refresh to get updated status
    } catch (error) {
      toast.error(error.response?.data?.error || "Échec de l'activation du contrat");
      console.error(error);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    try {
      await contractApi.update(id, { status: newStatus });
      toast.success('Statut mis à jour avec succès');
      fetchContract(); // Refresh to get updated status
    } catch (error) {
      toast.error(error.response?.data?.error || 'Échec de la mise à jour du statut');
      console.error(error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non spécifié';
    try {
      return format(new Date(dateString), 'dd MMMM yyyy', { locale: fr });
    } catch (error) {
      return dateString;
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { label: 'Brouillon', class: 'bg-gray-100 text-gray-800' },
      pending_signature: { label: 'En attente de signature', class: 'bg-yellow-100 text-yellow-800' },
      signed: { label: 'Signé', class: 'bg-blue-100 text-blue-800' },
      active: { label: 'Actif', class: 'bg-green-100 text-green-800' },
      terminated: { label: 'Résilié', class: 'bg-red-100 text-red-800' }
    };

    const config = statusConfig[status] || statusConfig.draft;

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.class}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return <div className="text-center py-8">Chargement du contrat...</div>;
  }

  if (!contract) {
    return <div className="text-center py-8">Contrat non trouvé</div>;
  }

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/contracts"
          className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-1" />
          Retour aux contrats
        </Link>

        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Contrat de Location</h1>
            <p className="text-gray-600 mt-1">Contrat #{contract.id}</p>
          </div>
          <div className="flex items-center space-x-3">
            {getStatusBadge(contract.status)}
            {contract.status === 'draft' && (
              <Link
                to={`/contracts/edit/${contract.id}`}
                className="btn-secondary inline-flex items-center"
              >
                <PencilSquareIcon className="h-5 w-5 mr-1" />
                Modifier
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Actions</h2>
        <div className="space-y-4">
          {/* Primary Actions */}
          <div className="flex flex-wrap gap-3">
            {/* Sign Contract Button */}
            {!contract.landlordSigned && (
              <Link
                to={`/contracts/${id}/sign`}
                className="btn-primary inline-flex items-center"
              >
                <PencilIcon className="h-5 w-5 mr-1" />
                Signer le contrat
              </Link>
            )}

            {/* Activate Contract Button */}
            {contract.status === 'signed' && contract.landlordSigned && contract.tenantSigned && (
              <button
                onClick={handleActivateContract}
                className="btn-primary inline-flex items-center bg-green-600 hover:bg-green-700"
              >
                <PlayIcon className="h-5 w-5 mr-1" />
                Activer le contrat
              </button>
            )}

            {/* PDF Actions */}
            <button
              onClick={handleGeneratePDF}
              className="btn-secondary inline-flex items-center"
            >
              <DocumentArrowDownIcon className="h-5 w-5 mr-1" />
              {contract.pdfPath ? 'Régénérer le PDF' : 'Générer le PDF'}
            </button>

            <button
              onClick={handleDownloadPDF}
              className="btn-secondary inline-flex items-center"
              disabled={!contract.pdfPath}
              title={!contract.pdfPath ? 'Veuillez d\'abord générer le PDF' : ''}
            >
              <DocumentArrowDownIcon className="h-5 w-5 mr-1" />
              Télécharger le PDF
            </button>

            <button
              onClick={() => setEmailModalOpen(true)}
              className="btn-secondary inline-flex items-center"
            >
              <EnvelopeIcon className="h-5 w-5 mr-1" />
              Envoyer par email
            </button>
          </div>

          {/* Status Change Options */}
          {contract.status !== 'active' && contract.status !== 'terminated' && (
            <div className="border-t pt-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Changer le statut:</p>
              <div className="flex flex-wrap gap-2">
                {contract.status === 'draft' && (
                  <button
                    onClick={() => handleUpdateStatus('pending_signature')}
                    className="px-3 py-1 text-sm bg-yellow-100 text-yellow-800 hover:bg-yellow-200 rounded-md"
                  >
                    En attente de signature
                  </button>
                )}
                {(contract.status === 'draft' || contract.status === 'pending_signature') && (
                  <button
                    onClick={() => handleUpdateStatus('signed')}
                    className="px-3 py-1 text-sm bg-blue-100 text-blue-800 hover:bg-blue-200 rounded-md"
                  >
                    Marquer comme signé
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Contract Details */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {/* Parties */}
        <div className="border-b border-gray-200 px-6 py-4 bg-gray-50">
          <h2 className="text-xl font-semibold text-gray-900">I. Désignation des parties</h2>
        </div>
        <div className="px-6 py-4 space-y-4">
          <div>
            <h3 className="font-medium text-gray-900 mb-2">Bailleur</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="font-medium">Nom:</span> {contract.landlordName}</div>
              <div><span className="font-medium">Type:</span> {contract.landlordType}</div>
              <div className="col-span-2"><span className="font-medium">Adresse:</span> {contract.landlordAddress}</div>
              {contract.landlordEmail && (
                <div><span className="font-medium">Email:</span> {contract.landlordEmail}</div>
              )}
            </div>
          </div>

          <div>
            <h3 className="font-medium text-gray-900 mb-2">Locataire</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="font-medium">Nom:</span> {contract.tenantFullName}</div>
              {contract.tenantEmail && (
                <div><span className="font-medium">Email:</span> {contract.tenantEmail}</div>
              )}
            </div>
          </div>
        </div>

        {/* Property Details */}
        <div className="border-b border-gray-200 px-6 py-4 bg-gray-50">
          <h2 className="text-xl font-semibold text-gray-900">II. Objet du contrat</h2>
        </div>
        <div className="px-6 py-4 space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-4">
            <div><span className="font-medium">Adresse:</span> {contract.propertyAddress}</div>
            <div><span className="font-medium">Surface:</span> {contract.propertySurface} m²</div>
            <div><span className="font-medium">Pièces:</span> {contract.propertyRoomsCount}</div>
            <div><span className="font-medium">Type:</span> {contract.propertyHabitatType}</div>
            <div><span className="font-medium">Chauffage:</span> {contract.propertyHeatingMode}</div>
            <div><span className="font-medium">DPE:</span> Classe {contract.propertyDpeClass}</div>
          </div>
        </div>

        {/* Duration */}
        <div className="border-b border-gray-200 px-6 py-4 bg-gray-50">
          <h2 className="text-xl font-semibold text-gray-900">III. Date et durée</h2>
        </div>
        <div className="px-6 py-4 space-y-2 text-sm">
          <div><span className="font-medium">Date de prise d'effet:</span> {formatDate(contract.contractStartDate)}</div>
          <div><span className="font-medium">Durée:</span> {contract.contractDuration} ans</div>
        </div>

        {/* Financial */}
        <div className="border-b border-gray-200 px-6 py-4 bg-gray-50">
          <h2 className="text-xl font-semibold text-gray-900">IV. Conditions financières</h2>
        </div>
        <div className="px-6 py-4 space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-4">
            <div><span className="font-medium">Loyer mensuel:</span> {contract.monthlyRent} €</div>
            {contract.chargesAmount > 0 && (
              <div><span className="font-medium">Charges:</span> {contract.chargesAmount} €</div>
            )}
            {contract.securityDeposit > 0 && (
              <div><span className="font-medium">Dépôt de garantie:</span> {contract.securityDeposit} €</div>
            )}
            <div><span className="font-medium">Périodicité:</span> {contract.paymentFrequency}</div>
            <div><span className="font-medium">Date de paiement:</span> {contract.paymentDate}</div>
          </div>
        </div>

        {/* Signature */}
        <div className="border-b border-gray-200 px-6 py-4 bg-gray-50">
          <h2 className="text-xl font-semibold text-gray-900">Signatures</h2>
        </div>
        <div className="px-6 py-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center">
              <span className="font-medium mr-2">Bailleur:</span>
              {contract.landlordSigned ? (
                <span className="inline-flex items-center text-green-600">
                  <CheckCircleIcon className="h-5 w-5 mr-1" />
                  Signé
                </span>
              ) : (
                <span className="inline-flex items-center text-gray-500">
                  <XMarkIcon className="h-5 w-5 mr-1" />
                  Non signé
                </span>
              )}
            </div>
            <div className="flex items-center">
              <span className="font-medium mr-2">Locataire:</span>
              {contract.tenantSigned ? (
                <span className="inline-flex items-center text-green-600">
                  <CheckCircleIcon className="h-5 w-5 mr-1" />
                  Signé
                </span>
              ) : (
                <span className="inline-flex items-center text-gray-500">
                  <XMarkIcon className="h-5 w-5 mr-1" />
                  Non signé
                </span>
              )}
            </div>
          </div>
          {contract.signatureDate && (
            <div className="mt-2 text-sm">
              <span className="font-medium">Date de signature:</span> {formatDate(contract.signatureDate)}
            </div>
          )}
        </div>
      </div>

      {/* Email Modal */}
      {emailModalOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Envoyer le contrat par email</h3>
            </div>
            <div className="px-6 py-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Adresse email
              </label>
              <input
                type="email"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                placeholder="email@exemple.com"
              />
            </div>
            <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3">
              <button
                onClick={() => setEmailModalOpen(false)}
                className="btn-secondary"
              >
                Annuler
              </button>
              <button
                onClick={handleSendEmail}
                className="btn-primary"
              >
                Envoyer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractDetail;
